import type { ReactNode } from 'react';

import { StatusDot } from '@/designSystem/atoms/StatusDot';
import { UnifiedIcon } from '@/components/UnifiedIcon';

export interface PreviewPanelProps {
  title?: string;
  subtitle?: string;
  children?: ReactNode;
  footer?: ReactNode;
  'data-testid'?: string;
}

/**
 * Right-column live preview shell (reference: LIVE PREVIEW + CPU illustration).
 */
export function PreviewPanel({
  title = 'Live preview',
  subtitle,
  children,
  footer,
  'data-testid': dataTestId,
}: PreviewPanelProps) {
  return (
    <aside
      data-testid={dataTestId ?? 'design-system-preview-panel'}
      className="ds-preview-panel flex flex-col p-5"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <StatusDot label="Live preview" data-testid={`${dataTestId ?? 'design-system-preview-panel'}-status`} />
          {subtitle ? <p className="mt-1 text-xs text-muted">{subtitle}</p> : null}
        </div>
      </div>

      <div
        data-testid={`${dataTestId ?? 'design-system-preview-panel'}-visual`}
        className="flex flex-1 flex-col items-center justify-center rounded-[var(--kc-radius-md)] border border-default bg-[var(--kc-bg-inset)] p-8"
      >
        {children ?? (
          <>
            <UnifiedIcon name="Cpu" size={64} className="text-accent opacity-90" />
            <p className="mt-4 text-center text-sm font-medium text-primary">{title}</p>
          </>
        )}
      </div>

      {footer ? (
        <div
          data-testid={`${dataTestId ?? 'design-system-preview-panel'}-footer`}
          className="mt-4 space-y-2 border-t border-default pt-4 text-sm"
        >
          {footer}
        </div>
      ) : null}
    </aside>
  );
}
